import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { uploadFile } from 'store';
import { KeyPurpose } from 'utils';
import { MinLoader } from '../loader';
import { buildCommentMention } from './commentMention';
import { serializeDocToText } from './commentUtils';

const IconAt = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" />
    </svg>
);
const IconAttach = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
);
const IconImprove = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#4F46E5"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
        <path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
    </svg>
);
const IconSend = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);
const IconRemove = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

/**
 * Rich comment composer built on the project's TipTap stack. Emits the comment
 * as plain text with inline `@[staffId]` mention tokens (backend format) plus a
 * list of uploaded StoredFile attachments.
 *
 * @param {(text: string, attachments: object[]) => void} onSubmit
 * @param {boolean} submitting     disables Post + shows a loader
 * @param {number}  resetSignal    bump this (e.g. on submit success) to clear the editor
 * @param {string}  attachmentPurpose  KeyPurpose bucket for uploads (default CHAT_ATTACHMENT)
 */
export const CommentEditor = ({
    placeholder = 'Write a comment...',
    onSubmit,
    submitting = false,
    resetSignal = 0,
    autoFocus = false,
    compact = false,
    onCancel,
    submitLabel = 'Post',
    enableAttachments = true,
    enableMentions = true,
    enableImprove = true,
    onImprove,
    attachmentPurpose = KeyPurpose.CHAT_ATTACHMENT,
    initialContent = '',
}) => {
    // Files are held locally as raw File objects and only uploaded to S3 on
    // submit (see handleSubmit) — nothing is stored server-side until the user
    // actually posts the comment.
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [hasContent, setHasContent] = useState(false);

    const extensions = useMemo(
        () => [
            StarterKit.configure({
                heading: false,
                codeBlock: false,
                blockquote: false,
                horizontalRule: false,
                hardBreak: true,
            }),
            Placeholder.configure({ placeholder }),
            // The mention popup is Redux-connected and paginates/searches staff itself.
            ...(enableMentions ? [buildCommentMention()] : []),
        ],
        // extensions are built once; the popup owns its own live data
        [],
    );

    const editor = useEditor({
        extensions,
        content: initialContent || '',
        autofocus: autoFocus,
        onUpdate: ({ editor }) => {
            setHasContent(!editor.isEmpty);
        },
    });

    // Reflect the initial content flag once the editor mounts (edit mode).
    useEffect(() => {
        if (editor && initialContent) setHasContent(!editor.isEmpty);
    }, [editor]);

    // Clear after a successful submit (parent bumps resetSignal).
    useEffect(() => {
        if (!editor || !resetSignal) return;
        editor.commands.clearContent();
        setAttachments((prev) => {
            prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
            return [];
        });
        setHasContent(false);
    }, [resetSignal]);

    // Stage files locally (images or any type) — no upload yet. Receives a File
    // (single) or FileList (multiple) from <FileUploader> handleChange.
    const handleAddFiles = (fileOrList) => {
        const files =
            fileOrList instanceof FileList ? Array.from(fileOrList) : Array.isArray(fileOrList) ? fileOrList : fileOrList ? [fileOrList] : [];
        setAttachments((prev) => [
            ...prev,
            ...files.map((file) => ({
                localId: `${file.name}-${file.size}-${file.lastModified}-${prev.length}`,
                originalName: file.name,
                isImage: (file.type || '').startsWith('image/'),
                previewUrl: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
                file,
            })),
        ]);
    };

    const removeAttachment = (localId) => {
        setAttachments((prev) => {
            const target = prev.find((a) => a.localId === localId);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((a) => a.localId !== localId);
        });
    };

    // Mirror attachments into a ref so the unmount cleanup can revoke blob
    // previews without touching state on an unmounting component.
    const attachmentsRef = useRef(attachments);
    attachmentsRef.current = attachments;
    useEffect(
        () => () => {
            attachmentsRef.current.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
        },
        [],
    );

    const canSubmit = (hasContent || attachments.length > 0) && !submitting && !uploading;

    const handleSubmit = async () => {
        if (!editor || !canSubmit) return;
        const text = serializeDocToText(editor.getJSON());
        if (!text && attachments.length === 0) return;

        // Upload every staged file now, then hand the resulting StoredFile array
        // to the parent. On any failure we keep the attachments so the user can
        // retry rather than losing their draft.
        let uploaded = [];
        if (attachments.length > 0) {
            setUploading(true);
            try {
                uploaded = await Promise.all(attachments.map((a) => uploadFile(a.file, attachmentPurpose)));
            } catch (err) {
                setUploading(false);
                return;
            }
            setUploading(false);
        }
        onSubmit?.(text, uploaded);
    };

    const handleImprove = () => {
        if (!editor) return;
        // Hook point for AI "Improve": wire to aiTransformActions.transformRichText
        // (streams via socket by conversationId — see store/aiTransform). Left as an
        // opt-in callback so the panel stays functional without the streaming setup.
        const text = serializeDocToText(editor.getJSON());
        onImprove?.(text, (improved) => editor.commands.setContent(improved || ''));
    };

    if (!editor) return null;

    return (
        <div className={`comment-editor ${compact ? 'comment-editor-compact' : ''}`}>
            <div className="comment-editor-input">
                <EditorContent editor={editor} />
            </div>

            {attachments.length > 0 && (
                <div className="comment-editor-attachments">
                    {attachments.map((a) => (
                        <div className="comment-attachment-chip" key={a.localId} title={a.originalName}>
                            {a.isImage && a.previewUrl ? (
                                <img className="thumb" src={a.previewUrl} alt={a.originalName} />
                            ) : (
                                <span className="dot" />
                            )}
                            <span className="name">{a.originalName}</span>
                            <button
                                type="button"
                                className="remove"
                                disabled={uploading}
                                onClick={() => removeAttachment(a.localId)}
                            >
                                <IconRemove />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="comment-editor-toolbar">
                <div className="comment-editor-tools">
                    {enableMentions && (
                        <button
                            type="button"
                            className="comment-tool-btn"
                            title="Mention someone"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => editor.chain().focus().insertContent('@').run()}
                        >
                            <IconAt />
                        </button>
                    )}
                    {enableAttachments && (
                        // App's proven uploader (react-drag-drop-files, same as UploadFiles).
                        // It owns the file-input click internally, so it opens in one click
                        // even inside the MUI Popover/modal focus trap.
                        <FileUploader handleChange={handleAddFiles} name="file" multiple classes="comment-attach-uploader">
                            <span className="comment-tool-btn" title="Attach file">
                                <IconAttach />
                            </span>
                        </FileUploader>
                    )}
                    {/*{enableImprove && (*/}
                    {/*    <button*/}
                    {/*        type="button"*/}
                    {/*        className="comment-tool-btn comment-tool-improve"*/}
                    {/*        title={onImprove ? 'Improve with AI' : 'AI improve (coming soon)'}*/}
                    {/*        disabled={!onImprove}*/}
                    {/*        onClick={handleImprove}*/}
                    {/*    >*/}
                    {/*        <IconImprove />*/}
                    {/*        <span>Improve</span>*/}
                    {/*    </button>*/}
                    {/*)}*/}
                </div>

                <div className="comment-editor-actions">
                    {onCancel && (
                        <button type="button" className="comment-cancel-btn" onClick={onCancel}>
                            Cancel
                        </button>
                    )}
                    <button
                        style={{width: 85}}
                        type="button"
                        className="comment-post-btn"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {submitting || uploading ? (
                            <MinLoader color="#FFFFFF" position="relative" margin="0" />
                        ) : (
                            <>
                                <span>{submitLabel}</span>
                                <IconSend />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
